import { CHAT } from "@eldrun/content";
import { ChatLog, completeChat, type ChatLine } from "./chat-model.ts";
import { h } from "../kit/dom.ts";

/**
 * Chat no HUD (doc 06): histórico à esquerda, acima dos atributos; Enter abre o campo (ou "/"
 * já com a barra), Enter envia, Esc fecha, Tab completa comandos e apelidos. Linhas novas somem
 * depois de 10 s com o campo fechado. Texto sempre via `textContent`.
 */
export class ChatBox {
  readonly element: HTMLElement;
  readonly log = new ChatLog();
  private readonly list: HTMLElement;
  private readonly form: HTMLFormElement;
  private readonly input: HTMLInputElement;
  private key = "";
  /** Apelidos para o Tab (lidos na hora). */
  names: () => string[] = () => [];
  /** Texto enviado (fala ou comando), já sem espaços nas pontas. */
  onSubmit: (text: string) => void = () => {};

  constructor(parent: HTMLElement) {
    this.list = h("ul", { class: "linhas", "aria-live": "polite" });
    this.input = h("input", { type: "text", maxlength: CHAT.maxLength, autocomplete: "off", spellcheck: "false", "aria-label": "Mensagem", placeholder: "Mensagem ou /ajuda" });
    this.form = h("form", { class: "campo" }, this.input);
    this.form.hidden = true;
    this.element = h("section", { id: "chat", "aria-label": "Chat" }, this.list, this.form);
    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      const text = this.input.value.trim();
      this.close();
      if (text) this.onSubmit(text);
    });
    // Clicou fora (de volta ao jogo): o campo fecha, e o Enter volta a abrir.
    this.input.addEventListener("blur", () => {
      if (this.open) this.close();
    });
    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        this.close();
      } else if (e.key === "Tab") {
        e.preventDefault();
        this.input.value = completeChat(this.input.value, this.names());
      }
    });
    parent.appendChild(this.element);
  }

  get open(): boolean {
    return !this.form.hidden;
  }

  show(prefill = ""): void {
    this.form.hidden = false;
    this.input.value = prefill;
    this.input.focus();
    this.key = "";
  }

  close(): void {
    this.form.hidden = true;
    this.input.value = "";
    this.input.blur();
    this.key = "";
  }

  push(line: ChatLine): void {
    if (this.log.push(line)) this.key = "";
  }

  /** Redesenha só quando o conjunto à vista muda (linhas novas, linhas que somem, abrir/fechar). */
  update(now: number): void {
    const lines = this.log.visible(now, this.open);
    const key = `${this.open ? 1 : 0}:${lines.length}:${lines.at(-1)?.at ?? 0}:${this.log.lines.length}`;
    if (key === this.key) return;
    this.key = key;
    this.list.replaceChildren(
      ...lines.map((l) => {
        const li = h("li", { class: `${l.kind}${l.dead ? " caido" : ""}` });
        if (l.kind === "fala") li.append(h("b", {}, `${l.dead ? "☠ " : ""}${l.name ?? "?"}: `), l.text);
        else li.append(l.text);
        return li;
      }),
    );
    this.list.scrollTop = this.list.scrollHeight;
    this.element.classList.toggle("aberto", this.open);
  }

  /** Quantas linhas estão à vista (testes). */
  get shown(): number {
    return this.list.childElementCount;
  }

  remove(): void {
    this.element.remove();
  }
}
